import { Container } from '@acies/core';
import Divider from './divider';
import IconRing from './icon-ring';
import { ICONS } from './icons';
import Label from './label';
import theme from './theme';

/**
 * The panel every dialog is built on.
 *
 * Only the look they share — a white card with a soft stroke, rounded corners and the shadow a popover has, stacking its content top to bottom — and a width it will not shrink below. What goes in it is the dialog's own, so each one extends this and adds its children to {@link Dialog.content}, its title and controls to
 * {@link Dialog.header}, and its actions to {@link Dialog.footer}.
 *
 * Meant to sit in a {@link FullscreenOverlay}, which centres it and dims what is behind it.
 */
export default class Dialog extends Container {
	/**
	 * The icon at the start of the header.
	 *
	 * Proxied to {@link Dialog.iconRing}, the way `IconTitleSection` proxies its icon to its header, so a dialog says which icon it has without reaching into the header for it.
	 */
	public set icon(value: keyof typeof ICONS | null) {
		this.iconRing.name = value;
	}

	public get icon(): keyof typeof ICONS | null {
		return this.iconRing.name;
	}

	/** The title of the dialog, proxied to {@link Dialog.textLabel} for the same reason {@link Dialog.icon} is proxied to the ring. */
	public set text(value: string) {
		this.textLabel.text = value;
	}

	public get text(): string {
		return this.textLabel.text;
	}

	#header!: Container;
	#iconRing!: IconRing;
	#textLabel!: Label;
	#topDivider!: Divider;
	#content!: Container;
	#bottomDivider!: Divider;
	#footer!: Container;
	public constructor() {
		super();
		this.minWidth = 400;
		this.autoLayout = 'vertical';
		this.cornerRadius = 16;
		this.fill = theme.colors.fill.white;
		this.stroke = theme.strokes.soft;
		this.effects = [theme.effects.shadows.light, theme.effects.shadows.medium];
	}

	/**
	 * The row across the top of the dialog, which a dialog puts its title and controls in.
	 *
	 * Here rather than in each dialog, so every dialog's header is the same height and has its content the same distance from the edge. It starts with {@link Dialog.iconRing}; what follows it is the dialog's own.
	 */
	public get header(): Container {
		if (!this.#header) {
			this.#header = new Container();
			this.#header.width = 'fill';
			this.#header.height = 64;
			this.#header.paddingHorizontal = 16;
			this.#header.gapHorizontal = 16;
			this.#header.alignment = 'left';
			this.#header.addComponents([this.iconRing, this.textLabel]);
		}
		return this.#header;
	}

	/** The icon at the start of the header, which says at a glance what kind of dialog this is. A dialog picks it through {@link Dialog.icon}. */
	public get iconRing(): IconRing {
		if (!this.#iconRing) {
			this.#iconRing = new IconRing();
		}
		return this.#iconRing;
	}

	/**
	 * The title of the dialog, after its icon.
	 *
	 * Filling the rest of the header, so anything a dialog adds after it is pushed to the right-hand end. Says `Dialog header text` until a dialog gives it its own through {@link Dialog.text}.
	 */
	public get textLabel(): Label {
		if (!this.#textLabel) {
			this.#textLabel = new Label();
			this.#textLabel.variant = 'dark';
			this.#textLabel.size = 'medium';
			this.#textLabel.width = 'fill';
			this.#textLabel.text = 'Dialog header text';
		}
		return this.#textLabel;
	}

	/** The line under the header, which sets it apart from what the dialog holds. */
	public get topDivider(): Divider {
		if (!this.#topDivider) {
			this.#topDivider = new Divider();
		}
		return this.#topDivider;
	}

	/**
	 * Where a dialog puts what it is about, under the header and its divider.
	 *
	 * Stacked top to bottom and padded in from the edges, so every dialog lays its content out the same way. At least tall enough to hold one row, so a dialog with little in it still has a body rather than collapsing onto its header.
	 */
	public get content(): Container {
		if (!this.#content) {
			this.#content = new Container();
			this.#content.width = 'fill';
			this.#content.autoLayout = 'vertical';
			this.#content.minHeight = 64;
			this.#content.paddingHorizontal = 16;
			this.#content.paddingVertical = 16;
			this.#content.gapVertical = 12;
		}
		return this.#content;
	}

	/** The line under the content, which sets it apart from whatever a dialog puts below it. */
	public get bottomDivider(): Divider {
		if (!this.#bottomDivider) {
			this.#bottomDivider = new Divider();
		}
		return this.#bottomDivider;
	}

	/**
	 * The row across the bottom of the dialog, which a dialog puts its actions in.
	 *
	 * Sized and padded like {@link Dialog.header}, so the two frame the content evenly, but lined up on the right: that is where the actions that close a dialog are looked for.
	 */
	public get footer(): Container {
		if (!this.#footer) {
			this.#footer = new Container();
			this.#footer.width = 'fill';
			this.#footer.height = 64;
			this.#footer.paddingHorizontal = 16;
			this.#footer.gapHorizontal = 16;
			this.#footer.alignment = 'right';
		}
		return this.#footer;
	}

	protected override initialize(): void {
		super.initialize();
		this.addComponents([this.header, this.topDivider, this.content, this.bottomDivider, this.footer]);
	}
}
customElements.define('ac-dialog', Dialog);
